'use client';

import React, {useState, useCallback} from 'react';
import {Modal, type ModalProps} from '@udemy-v2/react-dialog-components';
import {useI18n} from '@udemy-v2/i18n';
import {Button} from '@udemy-v2/react-core-components';
import {publishEvent, TrackImpression} from '@udemy-v2/event-tracking';
import classNames from 'classnames';
import {
    CodingExercisePromotionSeeDemoLinkClickEvent,
    CodingExercisePromotionDemoVideoImpressionEvent,
} from './events';
import dynamic from 'next/dynamic';

import {VideoPlayerProvider} from '../video-player/video-player-context';
import {useFetchAsset} from '@/lib/hooks/use-fetch-asset';
import styles from './coding-exercise-promotion-modal.module.scss';

// Dynamically import VideoPlayer to avoid SSR issues
const VideoPlayer = dynamic(() => import('../video-player/video-player.react-component'), {
    ssr: true,
});

interface CodingExercisePromotionModalProps {
    onClose: ModalProps['onClose'];
    isOpen: ModalProps['isOpen'];
    assetId: number;
    assetTitle: string;
    courseId: number;
    trackingId: string;
}

export const CodingExercisePromotionModal = ({
    onClose,
    isOpen,
    assetId,
    assetTitle,
    courseId,
    trackingId,
}: CodingExercisePromotionModalProps) => {
    const {gettext} = useI18n();
    const {assetVideo} = useFetchAsset(isOpen ? assetId : undefined, assetTitle);
    const trackImpression = useCallback(() => {
        publishEvent(
            new CodingExercisePromotionDemoVideoImpressionEvent({
                id: courseId,
                trackingId: trackingId,
            }),
        );
    }, [courseId, trackingId]);

    const renderContent = () => {
        return (
            <VideoPlayerProvider
                asset={assetVideo || undefined}
                durationInSeconds={assetVideo?.duration || 0}
            >
                <TrackImpression trackFunc={trackImpression} visibilityThreshold={0.25}>
                    <div className={styles['video-container']}>
                        <VideoPlayer autoplay={true} />
                    </div>
                </TrackImpression>
            </VideoPlayerProvider>
        );
    };

    return (
        <Modal
            className="ud-color-scheme-dark"
            renderTitle={() => ['title-in-body-modal-title', null]}
            title=""
            isOpen={isOpen}
            onClose={onClose}
        >
            <h2 id="coding-exercise-promotion-modal-title">
                <span className={classNames('ud-heading-lg', styles['demo-intro'])}>
                    {gettext('Coding Exercises Demo')}
                </span>
            </h2>
            {renderContent()}
        </Modal>
    );
};

interface CodingExerciseModalTriggerProps {
    assetTitle: string;
    assetId: number;
    courseId: number;
    ctaText: string;
    trackingId: string;
}

export const CodingExerciseModalTrigger = ({
    assetTitle,
    assetId,
    courseId,
    ctaText,
    trackingId,
}: CodingExerciseModalTriggerProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const trackCodingExerciseSeeDemoLinkClick = () => {
        publishEvent(
            new CodingExercisePromotionSeeDemoLinkClickEvent({
                id: courseId,
                trackingId: trackingId,
            }),
        );
    };

    const handleOpenModal = () => {
        trackCodingExerciseSeeDemoLinkClick();
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    return (
        <>
            <Button
                componentClass="a"
                className={styles['coding-exercise-modal-trigger']}
                udStyle="link-underline"
                typography="ud-text-sm"
                onClick={handleOpenModal}
            >
                {ctaText}
            </Button>
            <CodingExercisePromotionModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                assetId={assetId}
                assetTitle={assetTitle}
                courseId={courseId}
                trackingId={trackingId}
            />
        </>
    );
};
